// Popup form host for a single Criteria
// Checklist item. The modal owns LOCAL form state for every editable
// field; the server is only touched when the user clicks Save and Close.
// This kills three UX defects rooted in the prior auto-save
// pattern: (1) the Report-Uploaded checkbox needed two clicks to visually
// toggle, (2) Save and Close needed two clicks to actually close, and
// (3) the persisted "Attested by..." stamp leaked the raw user GUID.
//
// Decisions (do not relitigate):
//  - Local state is seeded ONCE per modal-open cycle. Background refetches
//    of the parent list query no longer clobber in-progress edits.
//  - Save submits the entire form-state object in ONE mutation; onSuccess
//    updates the cache, invalidates the parent roll-up, and closes the
//    modal — no chained two-phase save.
//  - The "Attested by..." stamp reads exclusively from server state. It is
//    an audit fact, not a preview of the user's unsaved checkbox toggle.
//  - The display name is resolved server-side in the read projection so
//    the frontend renders it verbatim (DisplayName → UserName → Email →
//    "Unknown user").

import { useEffect, useMemo, useReducer, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { ReportUploadedAttestationField } from "@/components/DealFlow/common/ReportUploadedAttestationField";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { checklistApi } from "@/api/dealFlow/client";
import { dealFlowKeys } from "@/hooks/dealFlow/useDealFlow";
import {
  reportGenerateDeepLink,
  useReportItemPrefill,
} from "@/hooks/reports/useReportItemPrefill";
import {
  CRITERIA_ITEM_ASSESSMENTS,
  type ActiveEscalationSummary,
  type CriteriaCatalogItemDto,
  type CriteriaChecklistItemDto,
  type CriteriaItemAssessment,
} from "@/api/dealFlow/types";
import {
  parseFormSchema,
  parseJustification,
  stringifyJustification,
  type JustificationData,
} from "@/lib/formSchema";
import type { ChecklistItemActions } from "@/lib/dealFlowPermissions";
import { PopupFormFromSchema } from "./PopupForms/PopupFormFromSchema";
import { PopupFormGeneric } from "./PopupForms/PopupFormGeneric";
import { CommonExpectedNote } from "./CommonExpectedNote";
import { EscalationStateBanner } from "./EscalationStateBanner";
import {
  approvedEscalationActions,
  classifyChecklistDirective,
} from "./escalationDirective";

interface CriteriaChecklistItemPopoverProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  item: CriteriaChecklistItemDto;
  catalog: CriteriaCatalogItemDto | undefined;
  /** What the server accepts from this caller on this item's lane. */
  actions: ChecklistItemActions;
}

type CriteriaFormState = {
  assessment: CriteriaItemAssessment | null;
  reportAttested: boolean;
  data: JustificationData;
};

type CriteriaFormAction =
  | { type: "init"; from: CriteriaChecklistItemDto }
  | { type: "setAssessment"; value: CriteriaItemAssessment | null }
  | { type: "setReportAttested"; value: boolean }
  | { type: "setData"; value: JustificationData };

function seed(item: CriteriaChecklistItemDto): CriteriaFormState {
  return {
    assessment: item.assessment,
    reportAttested: item.reportUploadedAttested,
    data: parseJustification(item.justification),
  };
}

function reducer(state: CriteriaFormState, action: CriteriaFormAction): CriteriaFormState {
  switch (action.type) {
    case "init":
      return seed(action.from);
    case "setAssessment":
      return { ...state, assessment: action.value };
    case "setReportAttested":
      return { ...state, reportAttested: action.value };
    case "setData":
      return { ...state, data: action.value };
  }
}

export function CriteriaChecklistItemPopover({
  open,
  onClose,
  dealId,
  item,
  catalog,
  actions,
}: CriteriaChecklistItemPopoverProps) {
  const schema = useMemo(
    () => parseFormSchema(catalog?.formSchema ?? null),
    [catalog?.formSchema],
  );
  // Notes saved from the generic box before this item's schema rendered
  // stay on screen beside the structured fields, so nothing typed is hidden.
  const hasEarlierNotes = useMemo(() => {
    const text = parseJustification(item.justification).__text;
    return typeof text === "string" && text.trim().length > 0;
  }, [item.justification]);

  const [form, dispatch] = useReducer(reducer, item, seed);

  // Item-level "Generate report" shortcut — resolves the item's catalog code
  // to its seeded report template (role-filtered server-side, same semantics
  // as the generator's own picker). No match (admin-added catalog item,
  // archived template, role mismatch) → no button. Convenience link only:
  // it never touches the analyst form or the certify/decline flow.
  const reportPrefill = useReportItemPrefill(item.code, dealId, open);

  // Local state is seeded once per modal-open cycle. A background refetch
  // of the parent list query (which swaps `item` to a new object reference
  // with the same id) MUST NOT re-init the form and clobber in-progress
  // edits. Re-seed only when the user opens a different row.
  const seededForIdRef = useRef<string>(item.id);
  useEffect(() => {
    if (item.id !== seededForIdRef.current) {
      dispatch({ type: "init", from: item });
      seededForIdRef.current = item.id;
    }
  }, [item]);

  const queryClient = useQueryClient();
  const toast = useToast();

  const onMutateSuccess = (updated: CriteriaChecklistItemDto) => {
    queryClient.setQueryData(
      dealFlowKeys.checklistItem(dealId, item.id),
      updated,
    );
    queryClient.invalidateQueries({
      queryKey: ["deal-flow", "checklist", dealId],
    });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      checklistApi.saveJustification(dealId, item.id, {
        justification: stringifyJustification(form.data),
        assessment: form.assessment,
        reportUploadedAttested: form.reportAttested,
      }),
    onSuccess: onMutateSuccess,
    onError: (err) => {
      toast.error(extractErrorMessage(err));
    },
  });

  // Post-escalation completion — explicit terminal actions for an item with
  // an Approved EscalationRecord. Each mutation first persists the current
  // local form state (so the analyst's severity / attestation / justification
  // are saved), then issues the terminal transition; the waive skips the save
  // for a caller who cannot save (E2). Save-and-Close remains permissive;
  // these handlers are the explicit way to progress an Escalated item to
  // Approved/Declined/Waived.
  const persistFormState = () =>
    checklistApi.saveJustification(dealId, item.id, {
      justification: stringifyJustification(form.data),
      assessment: form.assessment,
      reportUploadedAttested: form.reportAttested,
    });

  const certifyMutation = useMutation({
    mutationFn: async () => {
      await persistFormState();
      return checklistApi.certify(dealId, item.id);
    },
    onSuccess: onMutateSuccess,
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      await persistFormState();
      const reason = resolveTerminalReason(form.data, item.activeEscalation);
      return checklistApi.decline(dealId, item.id, { failureReason: reason });
    },
    onSuccess: onMutateSuccess,
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  // E2 waives on any lane but cannot save the form (the PUT needs
  // WriteAnalystTab), so a caller who cannot save sends the waive alone
  // (owner approval, 2026-10-13: "so E2 can waive").
  const waiveMutation = useMutation({
    mutationFn: async () => {
      if (actions.save) await persistFormState();
      const rationale = resolveTerminalReason(form.data, item.activeEscalation);
      return checklistApi.waive(dealId, item.id, { rationale });
    },
    onSuccess: onMutateSuccess,
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  // Status decides editability: once Approved / Declined / Waived the form
  // is locked (the row's "Open form" still surfaces it for read-back).
  const readOnly =
    item.status === "Approved" ||
    item.status === "Declined" ||
    item.status === "Waived";

  const allowed = approvedEscalationActions(item);
  const labels = terminalLabels(item);
  const anyMutationPending =
    save.isPending ||
    certifyMutation.isPending ||
    declineMutation.isPending ||
    waiveMutation.isPending;
  const missingAssessment = form.assessment == null;
  const missingAttestation = !form.reportAttested;
  const certifyDisabled = missingAssessment || missingAttestation;
  const certifyTooltip = certifyDisabled
    ? missingAssessment && missingAttestation
      ? "Select a Severity Assessment and confirm the report has been uploaded before certifying."
      : missingAssessment
        ? "Select a Severity Assessment before certifying."
        : "Confirm the associated report has been uploaded before certifying."
    : undefined;
  const declineDisabled = missingAttestation;
  const declineTooltip = declineDisabled
    ? "Confirm the associated report has been uploaded before declining."
    : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${item.code} — ${item.title}`}
      description={catalog?.description ?? undefined}
      size="lg"
      footer={
        <>
          {reportPrefill.data && (
            <a
              href={reportGenerateDeepLink(
                reportPrefill.data.templateId,
                dealId,
                item.code,
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto inline-flex items-center gap-space-1 text-small font-semibold text-onyx hover:text-gold-shadow"
            >
              <ExternalLink size={14} /> Generate report
            </a>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={anyMutationPending}
          >
            Close
          </Button>
          {!readOnly && actions.save && (
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={anyMutationPending}
            >
              {save.isPending ? "Saving…" : "Save and close"}
            </Button>
          )}
          {/* Post-escalation completion. Visible only once an executive has
              Approved the escalation: the actions the server accepts under
              its directive (one it pins, or every one when it pins none),
              narrowed by what this caller may do on the item (owner ruling,
              2026-10-26). Save-and-Close above stays permissive. */}
          {!readOnly && actions.certify && allowed.has("certify") && (
            <Button
              variant="primary"
              onClick={() => certifyMutation.mutate()}
              disabled={anyMutationPending || certifyDisabled}
              title={certifyTooltip}
            >
              {certifyMutation.isPending ? "Certifying…" : labels.certify}
            </Button>
          )}
          {!readOnly && actions.decline && allowed.has("decline") && (
            <Button
              variant="destructive"
              onClick={() => declineMutation.mutate()}
              disabled={anyMutationPending || declineDisabled}
              title={declineTooltip}
            >
              {declineMutation.isPending ? "Declining…" : labels.decline}
            </Button>
          )}
          {!readOnly && actions.waive && allowed.has("waive") && (
            <Button
              variant="secondary"
              onClick={() => waiveMutation.mutate()}
              disabled={anyMutationPending}
            >
              {waiveMutation.isPending ? "Waiving…" : labels.waive}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <EscalationStateBanner dealId={dealId} item={item} />
        {catalog?.isCommonExpected && (
          <CommonExpectedNote
            text={
              catalog.resolutionGuidance ??
              "DQR Reference notes this item is commonly observed in the small-business market; analysts should expect to see this 30-50% of the time and rely on context for severity calibration."
            }
          />
        )}

        {catalog?.requiredAction && (
          <section>
            <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
              Required action
            </h4>
            <p className="text-small text-onyx mt-space-1 m-0 whitespace-pre-line">
              {catalog.requiredAction}
            </p>
          </section>
        )}

        {catalog?.escalationPath && (
          <section>
            <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
              Escalation path
            </h4>
            <p className="text-small text-onyx mt-space-1 m-0">
              {catalog.escalationPath}
            </p>
          </section>
        )}

        <section>
          <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0 mb-space-2">
            Analyst form
          </h4>
          <div className="flex flex-col gap-space-3">
            <UniversalAssessmentField
              value={form.assessment}
              onChange={(v) => dispatch({ type: "setAssessment", value: v })}
              disabled={readOnly}
            />
            <ReportUploadedAttestationField
              checked={form.reportAttested}
              persistedAttested={item.reportUploadedAttested}
              persistedDisplayName={item.reportUploadedAttestedByDisplayName}
              persistedAttestedAt={item.reportUploadedAttestedAt}
              onChange={(v) => dispatch({ type: "setReportAttested", value: v })}
              disabled={readOnly}
            />
            {schema ? (
              <>
                <PopupFormFromSchema
                  schema={schema}
                  value={form.data}
                  onChange={(v) => dispatch({ type: "setData", value: v })}
                  disabled={readOnly}
                />
                {hasEarlierNotes && (
                  <PopupFormGeneric
                    value={form.data}
                    onChange={(v) => dispatch({ type: "setData", value: v })}
                    disabled={readOnly}
                  />
                )}
              </>
            ) : (
              <PopupFormGeneric
                value={form.data}
                onChange={(v) => dispatch({ type: "setData", value: v })}
                disabled={readOnly}
              />
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Post-escalation completion — the footer labels. The Escalation Detail
// Drawer offers four presets (Proceed with Waiver / Decline this Item /
// Require Additional Documentation / Approve as Pass) and the executive may
// type any directive; `approvedEscalationActions` decides which buttons show,
// exactly as the server decides what it accepts. A directive that pins the
// action labels it "per Directive"; one that pins none (e.g. "Return to
// Pending", which the server does not apply to an approved escalation) leaves
// the analyst's choice, so the buttons carry plain verbs. A Denied escalation
// is handled by the "Return to Pending and rework" button in
// <EscalationStateBanner>; the modal footer stays quiet.
// ----------------------------------------------------------------------

function terminalLabels(item: CriteriaChecklistItemDto): {
  certify: string;
  decline: string;
  waive: string;
} {
  const directive = item.activeEscalation?.resolutionDirective ?? "";
  if (classifyChecklistDirective(directive) === "any") {
    return { certify: "Certify", decline: "Decline", waive: "Waive" };
  }
  const d = directive.toLowerCase();
  return {
    certify:
      d.includes("documentation") || d.includes("documents")
        ? "Certify (Documentation Provided)"
        : "Certify per Directive",
    decline: "Decline per Directive",
    waive: "Waive per Directive",
  };
}

function resolveTerminalReason(
  data: JustificationData,
  escalation: ActiveEscalationSummary | null,
): string {
  const fromForm = stringifyJustification(data);
  if (fromForm && fromForm.trim().length > 0) return fromForm;
  const fromNotes = escalation?.resolutionNotes?.trim();
  if (fromNotes) return fromNotes;
  const fromDirective = escalation?.resolutionDirective?.trim();
  if (fromDirective) return `Per executive directive: ${fromDirective}`;
  return "Closed per executive escalation resolution.";
}

// ----------------------------------------------------------------------
// Universal modal fields. Rendered above the per-item schema-driven
// form on every Open-Form popup, regardless of catalog item.
// ----------------------------------------------------------------------

function UniversalAssessmentField({
  value,
  onChange,
  disabled,
}: {
  value: CriteriaItemAssessment | null;
  onChange: (next: CriteriaItemAssessment | null) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <label
        htmlFor="criteria-severity-assessment"
        className="font-display text-label uppercase tracking-label text-gold-shadow m-0"
      >
        Severity assessment <span className="text-status-error">*</span>
      </label>
      <select
        id="criteria-severity-assessment"
        className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure disabled:bg-white-warm disabled:text-gray-mid"
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            e.target.value === ""
              ? null
              : (e.target.value as CriteriaItemAssessment),
          )
        }
        disabled={disabled}
      >
        <option value="">Select an assessment…</option>
        {CRITERIA_ITEM_ASSESSMENTS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <p className="text-caption text-gray-mid m-0">
        Required to certify this item. Captures the analyst's judgment of how
        serious this finding is.
      </p>
    </div>
  );
}
